import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BASE_URL } from "../../api";
import { markNotificationScopeRead } from "../../utils/notificationsApi";
import "./DocumentsPage.css";

function DocumentsPage() {
  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("role");
  const selectedProfilId = localStorage.getItem("selectedProfilId") || "";
  const navigate = useNavigate();

  const RAW =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
    BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "";
  const API_BASE = (RAW ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "") : "") || "http://localhost:5000";
  const token = localStorage.getItem("token");
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [activeTab, setActiveTab] = useState("sent");
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState("facture");
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [appointments, setAppointments] = useState([]);

  const [showFactureForm, setShowFactureForm] = useState(false);
  const [factureLines, setFactureLines] = useState([{ description: "", quantite: 1, prix: 0, tva: 0 }]);
  const [clientInfo, setClientInfo] = useState(null);
  const [proInfo, setProInfo] = useState(null);
  const [profilActivities, setProfilActivities] = useState([]);
  const [topText, setTopText] = useState("");
  const [bottomText, setBottomText] = useState("");
  const [tvaNotApplicable, setTvaNotApplicable] = useState(false);
  const [factureNumber, setFactureNumber] = useState("");
  const [companyName, setCompanyName] = useState("");

  const signatureRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [sigDirty, setSigDirty] = useState(false);

  const [stampDataUrl, setStampDataUrl] = useState(null);

  useEffect(() => {
    markNotificationScopeRead("documents");
  }, []);

  useEffect(() => {
    const abonnement = localStorage.getItem("abonnement")?.toLowerCase();
    const roleLocal = localStorage.getItem("role")?.toLowerCase();
    if (roleLocal === "pro") {
      if (!abonnement) {
        alert("Veuillez sélectionner un abonnement pour accéder à cette page.");
        navigate("/billing/subscription");
      } else if (abonnement === "basic") {
        alert("Votre abonnement actuel ne vous donne pas accès aux documents. Veuillez changer d'offre.");
        navigate("/billing/subscription");
      }
    }
  }, [navigate]);

  const fetchCompanyNameForUser = async (uid) => {
    if (!uid) return;
    try {
      let res = await fetch(`${API_BASE}/api/annonces/user-one/${uid}`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.nameCompany) {
          setCompanyName(data.nameCompany);
          return;
        }
      }
      res = await fetch(`${API_BASE}/api/annonces/by-user/${uid}`);
      if (res.ok) {
        const list = await res.json();
        const first = Array.isArray(list) ? list.find((a) => a && a.nameCompany) : null;
        if (first?.nameCompany) {
          setCompanyName(first.nameCompany);
          return;
        }
      }
    } catch {
      // ignore
    }
    setCompanyName((prev) => prev || proInfo?.societe || proInfo?.nameCompany || "");
  };

  const fetchDocuments = async () => {
    try {
      let url =
        role === "pro" ? `${API_BASE}/api/pro/documents/${userId}` : `${API_BASE}/api/client/documents/${userId}`;
      if (selectedProfilId) url += `?profilId=${selectedProfilId}`;
      const res = await fetch(url, { headers: { ...authHeader } });
      const data = await res.json();
      setDocuments(data);
    } catch (err) {
      console.error("Erreur récupération documents :", err);
    }
  };

  const fetchAppointments = async () => {
    try {
      let endpoint = "";
      if (role === "pro") {
        endpoint = `${API_BASE}/api/slotsCalendar/pro/${userId}`;
        if (selectedProfilId) endpoint += `?profilId=${selectedProfilId}`;
      } else {
        endpoint = `${API_BASE}/api/slotsCalendar/user/${userId}`;
      }
      const res = await fetch(endpoint, { headers: { ...authHeader } });
      const data = await res.json();
      const formatted = data.map((slot) => ({
        ...slot,
        receiverId: role === "pro" ? slot.userId?._id || slot.userId : slot.proId?._id || slot.proId,
      }));
      setAppointments(formatted);
    } catch (err) {
      console.error("Erreur récupération RDV :", err);
    }
  };

  useEffect(() => {
    const fetchProInfo = async () => {
      if (role === "pro") {
        try {
          const res = await fetch(`${API_BASE}/api/users/${userId}`, { headers: { ...authHeader } });
          const data = await res.json();
          setProInfo(data);
        } catch {
          setProInfo(null);
        } finally {
          await fetchCompanyNameForUser(userId);
        }
      }
    };
    fetchProInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, userId]);

  useEffect(() => {
    if (userId) {
      fetchDocuments();
      fetchAppointments();
    }
    // eslint-disable-next-line
  }, [userId, selectedProfilId, role]);

  const handleSelectRdv = async (value) => {
    if (!value) {
      setSelectedRdv(null);
      setClientInfo(null);
      setProfilActivities([]);
      return;
    }
    const rdv = JSON.parse(value);
    setSelectedRdv(rdv);

    if (role === "pro") {
      setClientInfo(rdv.userId);
    } else {
      try {
        const res = await fetch(`${API_BASE}/api/users/${rdv.proId}`, { headers: { ...authHeader } });
        const data = await res.json();
        setClientInfo(data);
        setProInfo(data);
      } catch {
        setClientInfo(null);
        setProInfo(null);
      }
      await fetchCompanyNameForUser(rdv.proId);
    }

    if (rdv.profilId) {
      try {
        const resProfil = await fetch(`${API_BASE}/api/annonces/profils/${rdv.profilId}`);
        const profil = await resProfil.json();
        setProfilActivities(Array.isArray(profil.activities) ? profil.activities : []);
      } catch {
        setProfilActivities([]);
      }
    } else {
      setProfilActivities([]);
    }
  };

  const filteredDocs = Array.isArray(documents)
    ? documents.filter((doc) => {
        const sid = String(doc.senderId?._id || doc.senderId);
        const rid = String(doc.receiverId?._id || doc.receiverId);
        const uid = String(userId);
        return activeTab === "sent" ? sid === uid : rid === uid;
      })
    : [];

  const addFactureLine = () => {
    setFactureLines([...factureLines, { description: "", quantite: 1, prix: 0, tva: 0 }]);
  };
  const removeLine = (index) => {
    const updated = [...factureLines];
    updated.splice(index, 1);
    setFactureLines(updated);
  };

  const totalFacture = factureLines.reduce((total, line) => {
    const totalLigne = line.quantite * line.prix;
    return total + totalLigne + (tvaNotApplicable ? 0 : totalLigne * (line.tva / 100));
  }, 0);

  useEffect(() => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    const cssWidth = 500;
    const cssHeight = 160;
    canvas.style.width = cssWidth + "px";
    canvas.style.height = cssHeight + "px";
    canvas.width = Math.floor(cssWidth * ratio);
    canvas.height = Math.floor(cssHeight * ratio);
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cssWidth, cssHeight);
  }, [showFactureForm]);

  const getCanvasPos = (evt) => {
    const canvas = signatureRef.current;
    const rect = canvas.getBoundingClientRect();
    let x, y;
    if (evt.touches && evt.touches[0]) {
      x = evt.touches[0].clientX - rect.left;
      y = evt.touches[0].clientY - rect.top;
    } else {
      x = evt.clientX - rect.left;
      y = evt.clientY - rect.top;
    }
    return { x, y };
  };

  const startDraw = (e) => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setSigDirty(true);
  };
  const moveDraw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endDraw = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = signatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    const ratio = window.devicePixelRatio || 1;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    setSigDirty(false);
  };

  const onStampChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setStampDataUrl(reader.result);
    reader.readAsDataURL(f);
  };

  const buildPdfDoc = () => {
    const doc = new jsPDF();

    const bigName = companyName || proInfo?.nameCompany || proInfo?.societe || "";

    if (bigName) {
      doc.setFontSize(20);
      doc.text(bigName, 105, 16, { align: "center" });
    }

    const titre = fileType === "facture" ? "FACTURE" : "DEVIS";
    const numero = factureNumber ? `n° ${factureNumber}` : "";
    const now = new Date();
    const today = now.toLocaleDateString("fr-FR");

    doc.setFontSize(18);
    doc.text(`${titre} ${numero}`, 105, bigName ? 26 : 18, { align: "center" });

    doc.setFontSize(12);
    doc.text(`Le ${today}`, 200, bigName ? 26 : 18, { align: "right" });

    const headerBottomY = bigName ? 34 : 26;
    let yPro = headerBottomY;
    doc.text(`Professionnel: ${proInfo?.prenom || ""} ${proInfo?.nom || ""}`, 10, yPro);
    if (proInfo?.siret) {
      yPro += 6;
      doc.text(`SIRET: ${proInfo.siret}`, 10, yPro);
    }
    if (proInfo?.telephone) {
      yPro += 6;
      doc.text(`Téléphone: ${proInfo.telephone}`, 10, yPro);
    }
    if (proInfo?.ville) {
      yPro += 6;
      doc.text(`Ville: ${proInfo.ville}`, 10, yPro);
    }
    if (proInfo?.adresse) {
      yPro += 6;
      doc.text(`Adresse: ${proInfo.adresse}`, 10, yPro);
    }
    if (proInfo?.email) {
      yPro += 6;
      doc.text(`Email: ${proInfo.email}`, 10, yPro);
    }

    let yCli = headerBottomY;
    doc.text(`Client: ${clientInfo?.prenom || ""} ${clientInfo?.nom || ""}`, 130, yCli);
    if (clientInfo?.telephone) {
      yCli += 6;
      doc.text(`Téléphone: ${clientInfo.telephone}`, 130, yCli);
    }
    if (clientInfo?.ville) {
      yCli += 6;
      doc.text(`Ville: ${clientInfo.ville}`, 130, yCli);
    }
    if (clientInfo?.adresse) {
      yCli += 6;
      doc.text(`Adresse: ${clientInfo.adresse}`, 130, yCli);
    }
    if (clientInfo?.email) {
      yCli += 6;
      doc.text(`Email: ${clientInfo.email}`, 130, yCli);
    }

    const startY = Math.max(yPro, yCli) + 10;
    if (topText) doc.text(topText, 10, startY);

    const rows = factureLines.map((line, index) => {
      const ht = line.quantite * line.prix;
      const tvaAmount = tvaNotApplicable ? 0 : ht * (line.tva / 100);
      const ttc = ht + tvaAmount;
      return [
        index + 1,
        line.description,
        line.quantite,
        `${line.prix.toFixed(2)}€`,
        `${ht.toFixed(2)}€`,
        tvaNotApplicable ? "0%" : `${line.tva}%`,
        `${ttc.toFixed(2)}€`,
      ];
    });

    autoTable(doc, {
      startY: startY + 5,
      head: [["#", "Description", "Qté", "PU (€)", "HT (€)", "TVA", "TTC (€)"]],
      body: rows,
    });

    let y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text(`Total à payer : ${totalFacture.toFixed(2)} €`, 10, y);

    if (tvaNotApplicable) {
      doc.setFontSize(12);
      doc.text("TVA non applicable — article 293B du CGI", 105, y + 10, { align: "center" });
    }

    if (bottomText) {
      doc.setFontSize(10);
      doc.text(bottomText, 10, y + 20);
    }

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let placeY = y + 30;
    if (placeY > pageH - 70) placeY = pageH - 70;

    if (sigDirty && signatureRef.current) {
      const sigUrl = signatureRef.current.toDataURL("image/png");
      try {
        const props = doc.getImageProperties(sigUrl);
        const targetW = 60;
        const targetH = (props.height / props.width) * targetW;
        doc.setFontSize(10);
        doc.text("Signature :", 10, placeY - 4);
        doc.addImage(sigUrl, "PNG", 10, placeY, targetW, targetH);
      } catch {
        // ignore
      }
    }

    if (stampDataUrl) {
      const imgType = stampDataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
      try {
        const props = doc.getImageProperties(stampDataUrl);
        const targetW = 35;
        const targetH = (props.height / props.width) * targetW;
        const x = pageW - 10 - targetW;
        doc.setFontSize(10);
        doc.text("Tampon :", x, placeY - 4, { align: "right" });
        doc.addImage(stampDataUrl, imgType, x, placeY, targetW, targetH);
      } catch {
        // ignore
      }
    }

    return doc;
  };

  const generatePdf = () => {
    const doc = buildPdfDoc();
    const titre = fileType === "facture" ? "facture" : "devis";
    doc.save(`${titre}-${factureNumber || Date.now()}.pdf`);
  };

  const handleSaveAndSend = async () => {
    const receiverId = clientInfo && (clientInfo._id || clientInfo);
    if (!receiverId) {
      alert("Veuillez sélectionner un rendez-vous (client) pour envoyer la facture.");
      return;
    }
    try {
      const doc = buildPdfDoc();
      const titre = fileType === "facture" ? "facture" : "devis";
      const filename = `${titre}-${factureNumber || Date.now()}.pdf`;
      const blob = doc.output("blob", { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", blob, filename);
      formData.append("fileType", fileType);
      formData.append("receiverId", receiverId);

      const docsPath = role === "pro" ? "/api/pro/documents" : "/api/client/documents";
      const res = await fetch(`${API_BASE}${docsPath}`, {
        method: "POST",
        headers: { ...authHeader },
        body: formData,
      });

      if (!res.ok) throw new Error("Erreur envoi document");
      alert("✅ Facture enregistrée et envoyée au client !");
      setShowFactureForm(false);
      fetchDocuments();
    } catch (err) {
      console.error("Erreur Enregistrer et envoyer :", err);
      alert("❌ Erreur lors de l'enregistrement et de l'envoi.");
    }
  };

  return (
    <div className="documents-page">
      <h1>📁 Mes Documents</h1>
      {showFactureForm ? (
        <div className="facture-form">
          <div className="form-row">
            <label>Type :</label>
            <select value={fileType} onChange={(e) => setFileType(e.target.value)}>
              <option value="facture">Facture</option>
              <option value="devis">Devis</option>
            </select>
          </div>
          <h3>{fileType === "facture" ? "🧾 Nouvelle facture" : "📝 Nouveau devis"}</h3>

          {companyName ? (
            <div className="form-row">
              <label>Nom de la société :</label>
              <input type="text" value={companyName} readOnly />
            </div>
          ) : null}

          <div className="form-row">
            <label>Choisir un rendez-vous (client) :</label>
            <select onChange={(e) => handleSelectRdv(e.target.value)} value={selectedRdv ? JSON.stringify(selectedRdv) : ""}>
              <option value="">-- Choisir un client --</option>
              {appointments.map((slot) => {
                const client = slot.userId && typeof slot.userId === "object" ? slot.userId : {};
                const start = new Date(slot.start);
                const end = new Date(slot.end);
                const dateString = start.toLocaleDateString();
                const startTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                const endTime = end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                return (
                  <option key={slot._id} value={JSON.stringify(slot)}>
                    {slot.title} — {dateString} de {startTime} à {endTime}
                    {client.prenom && client.nom ? ` — ${client.prenom} ${client.nom}` : ""}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-row">
            <label>Numéro de facture ou devis (facultatif) :</label>
            <input
              type="text"
              value={factureNumber}
              onChange={(e) => setFactureNumber(e.target.value)}
              placeholder={fileType === "facture" ? "FAC-2025-001" : "DEV-2025-001"}
            />
          </div>

          <div className="form-row">
            <label>Texte en haut (facultatif) :</label>
            <textarea value={topText} onChange={(e) => setTopText(e.target.value)} />
          </div>

          <table className="facture-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qté</th>
                <th>PU (€)</th>
                <th>HT (€)</th>
                <th>TVA (%)</th>
                <th>TTC (€)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {factureLines.map((line, i) => {
                const ht = line.quantite * line.prix;
                const ttc = tvaNotApplicable ? ht : ht + ht * (line.tva / 100);
                return (
                  <tr key={i}>
                    <td>
                      <select
                        value={line.description}
                        onChange={(e) => {
                          const updated = [...factureLines];
                          updated[i].description = e.target.value;
                          const selectedActivity = profilActivities.find((activity) => activity.name === e.target.value);
                          if (selectedActivity) {
                            updated[i].prix = selectedActivity.price || selectedActivity.prix || 0;
                            updated[i].tva = selectedActivity.tva || selectedActivity.taxe || 0;
                          }
                          setFactureLines(updated);
                        }}
                      >
                        <option value="">-- Choisir une activité --</option>
                        {profilActivities.map((activity, idx) => (
                          <option key={activity._id || idx} value={activity.name}>
                            {activity.name}
                            {activity.durationMinutes ? ` (${activity.durationMinutes} min)` : ""}
                            {activity.price || activity.prix ? ` — ${activity.price || activity.prix}€` : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        value={line.quantite}
                        onChange={(e) => {
                          const updated = [...factureLines];
                          updated[i].quantite = parseInt(e.target.value, 10);
                          setFactureLines(updated);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={line.prix}
                        onChange={(e) => {
                          const updated = [...factureLines];
                          updated[i].prix = parseFloat(e.target.value);
                          setFactureLines(updated);
                        }}
                      />
                    </td>
                    <td>{ht.toFixed(2)} €</td>
                    <td>
                      <input
                        type="number"
                        value={line.tva}
                        disabled={tvaNotApplicable}
                        onChange={(e) => {
                          const updated = [...factureLines];
                          updated[i].tva = parseFloat(e.target.value);
                          setFactureLines(updated);
                        }}
                      />
                    </td>
                    <td>{ttc.toFixed(2)} €</td>
                    <td>
                      <button onClick={() => removeLine(i)}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <button onClick={addFactureLine}>➕ Ajouter une ligne</button>

          <div className="form-row tva-row" style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <input
              type="checkbox"
              checked={tvaNotApplicable}
              onChange={(e) => setTvaNotApplicable(e.target.checked)}
              id="tva-checkbox"
            />
            <label htmlFor="tva-checkbox" style={{ fontWeight: 700 }}>
              TVA non applicable
            </label>

            <button
              type="button"
              aria-pressed={tvaNotApplicable}
              onClick={() => setTvaNotApplicable((v) => !v)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                border: tvaNotApplicable ? "1px solid rgba(22,163,74,.55)" : "1px solid #d7d7d7",
                background: tvaNotApplicable ? "rgba(22,163,74,.12)" : "#fff",
                color: tvaNotApplicable ? "#166534" : "#6b7280",
                fontWeight: 800,
                letterSpacing: ".2px",
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,.06)",
              }}
              title={
                tvaNotApplicable
                  ? "TVA non applicable activée — cliquer pour désactiver"
                  : "Cliquer pour activer la TVA non applicable"
              }
            >
              {tvaNotApplicable ? "✅ TVA non applicable activée — cliquer pour désactiver" : "Cliquer ici pour la TVA non applicable"}
            </button>
          </div>

          <div className="form-row" style={{ flexDirection: "column", gap: 10 }}>
            <label style={{ fontWeight: 600 }}>Signature (facultatif — souris / tactile)</label>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ border: "1px dashed #bbb", borderRadius: 8, padding: 8, background: "#fff" }}>
                <canvas
                  ref={signatureRef}
                  onMouseDown={startDraw}
                  onMouseMove={moveDraw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={moveDraw}
                  onTouchEnd={endDraw}
                  style={{ cursor: "crosshair", background: "#fff", display: "block" }}
                />
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button type="button" onClick={clearSignature}>
                    Effacer
                  </button>
                </div>
              </div>

              <div style={{ minWidth: 240 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Tampon (facultatif — image sur feuille blanche)
                </label>
                <input type="file" accept="image/*" onChange={onStampChange} />
                {stampDataUrl ? (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Aperçu :</div>
                    <img
                      src={stampDataUrl}
                      alt="Tampon"
                      style={{
                        maxWidth: 200,
                        maxHeight: 120,
                        border: "1px solid #eee",
                        borderRadius: 6,
                        background: "#fff",
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="form-row">
            <label>Texte bas (facultatif) :</label>
            <textarea value={bottomText} onChange={(e) => setBottomText(e.target.value)} />
          </div>

          <h4>Total à payer : {totalFacture.toFixed(2)} €</h4>
          <button onClick={generatePdf}>📥 Générer le PDF</button>
          <button onClick={handleSaveAndSend} className="save-send-btn">
            💾 Enregistrer et envoyer
          </button>
          <button style={{ marginTop: 16 }} onClick={() => setShowFactureForm(false)}>
            ❌ Fermer facture
          </button>
        </div>
      ) : (
        <>
          <div className="tab-buttons">
            <button onClick={() => setActiveTab("sent")} className={activeTab === "sent" ? "active" : ""}>
              Envoyés
            </button>
            <button onClick={() => setActiveTab("received")} className={activeTab === "received" ? "active" : ""}>
              Reçus
            </button>
            {role === "pro" ? <button onClick={() => setShowFactureForm(true)}>🧾 Créer une facture</button> : null}
          </div>
          {activeTab === "sent" ? (
            <div className="upload-section">
              <h3>📤 Envoyer un document</h3>
              <div className="upload-row">
                <label>Type :</label>
                <select value={fileType} onChange={(e) => setFileType(e.target.value)}>
                  <option value="facture">Facture</option>
                  <option value="devis">Devis</option>
                  <option value="autres">Autres</option>
                </select>
              </div>
              <div className="upload-row">
                <label>Choisir un fichier :</label>
                <input type="file" onChange={(e) => setFile(e.target.files[0])} />
              </div>
              <div className="upload-row">
                <label>Envoyer à :</label>
                <select
                  className="upload-select"
                  onChange={(e) => setSelectedRdv(JSON.parse(e.target.value))}
                  value={selectedRdv ? JSON.stringify(selectedRdv) : ""}
                >
                  <option value="">-- Sélectionner un rendez-vous --</option>
                  {appointments.map((slot) => {
                    const userObj =
                      role === "pro"
                        ? slot.userId && typeof slot.userId === "object"
                          ? slot.userId
                          : {}
                        : slot.proId && typeof slot.proId === "object"
                        ? slot.proId
                        : {};

                    const fullName =
                      userObj.prenom && userObj.nom ? `${userObj.prenom} ${userObj.nom}` : userObj.nameCompany || "";

                    const start = new Date(slot.start);
                    const end = new Date(slot.end);
                    const dateString = start.toLocaleDateString();
                    const startTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    const endTime = end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                    return (
                      <option
                        key={slot._id}
                        value={JSON.stringify({
                          receiverId: role === "pro" ? slot.userId?._id || slot.userId : slot.proId?._id || slot.proId,
                          rdvId: slot._id,
                          start: slot.start,
                          title: slot.title,
                        })}
                      >
                        {slot.title} — {dateString} {startTime} - {endTime}
                        {fullName ? ` — ${fullName}` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="upload-row" style={{ justifyContent: "flex-end" }}>
                <button
                  className="upload-send-btn"
                  onClick={async () => {
                    if (!file || !selectedRdv) return alert("Tous les champs sont requis");
                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("fileType", fileType);
                    formData.append("receiverId", selectedRdv.receiverId);
                    if (selectedProfilId) formData.append("profilId", selectedProfilId);

                    try {
                      const docsPath = role === "pro" ? "/api/pro/documents" : "/api/client/documents";
                      const res = await fetch(`${API_BASE}${docsPath}`, {
                        method: "POST",
                        headers: { ...authHeader },
                        body: formData,
                      });

                      if (!res.ok) throw new Error("Erreur upload");

                      alert("✅ Document envoyé !");
                      setFile(null);
                      setSelectedRdv(null);
                      fetchDocuments();
                    } catch (err) {
                      console.error("Erreur d’envoi :", err);
                      alert("❌ Erreur d’envoi du document");
                    }
                  }}
                >
                  Envoyer
                </button>
              </div>
            </div>
          ) : null}
          <div className="documents-list">
            <h4>📜 Documents {activeTab === "sent" ? "envoyés" : "reçus"}</h4>
            <ul>
              {filteredDocs.map((doc) => {
                const fileName = doc.fileName || (doc.filePath && doc.filePath.split("/").pop()) || "document";
                const downloadUrl = `${API_BASE}/uploads/${fileName}`;
                return (
                  <li key={doc._id}>
                    <a href={downloadUrl} target="_blank" rel="noreferrer">
                      {fileName} ({doc.fileType})
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default DocumentsPage;


